/*
  Aromaperfuma shared behaviour: cart, product forms, variant picker, quantity,
  dialogs, quick add, carousels, language switch and small helpers.
  Components in sections/blocks may use window.Aroma at event time.

  Contracts:
    [data-product-root]            wraps one product's picker, price and form
    <variant-picker>               radios + <script type="application/json" data-variants>
    <product-form>                 wraps {% form 'product' %}; adds to the bag without a page load
    [data-price-amount] [data-price-compare] [data-price-wrap]   updated on variant change
    [data-cart-section="ID"] [data-cart-region="name"]          re-rendered after cart changes
    [data-cart-count] [data-cart-count-wrap]                    bag counts
    [data-line-key] + [data-line-quantity] / [data-line-remove] cart line controls
    [data-dialog-open="ID"] [data-dialog-close]                 open and close <dialog> elements
    [data-quick-add="URL"] [data-variant-id]                    quick add from a card
*/
(function () {
  'use strict';

  var Aroma = window.Aroma = window.Aroma || {};
  var config = Aroma.config || {};
  var strings = config.strings || {};
  var root = document.documentElement;

  /* Helpers */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  function emit(name, detail, target) {
    (target || document).dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || config.moneyFormat || '{{amount}}';
    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';
    var value;
    switch (placeholder) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.'); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'"); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* The theme prints currency and amount separately so Arabic can read currency first.
     This matches the Liquid price snippet: whole numbers drop ".00". */
  function formatAmount(cents) {
    var amount = formatMoney(cents, '{{amount}}');
    return amount.replace(/\.00$/, '');
  }

  function toast(message, options) {
    options = options || {};
    var region = document.querySelector('[data-toast-region]');
    if (!region) return;
    var el = document.createElement('div');
    el.className = 'toast';
    var check = '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
    var text = document.createElement('span');
    text.textContent = message;
    el.innerHTML = '<span class="toast__message">' + (options.icon === false ? '' : check) + '</span>';
    el.firstChild.appendChild(text);
    if (options.actionLabel) {
      var action = document.createElement(options.actionHref ? 'a' : 'button');
      action.className = 'toast__action';
      action.textContent = options.actionLabel;
      if (options.actionHref) action.href = options.actionHref;
      if (options.onAction) action.addEventListener('click', function (event) { options.onAction(event); dismiss(); });
      el.appendChild(action);
    }
    region.innerHTML = '';
    region.appendChild(el);
    var timer = setTimeout(dismiss, options.duration || 4200);
    function dismiss() {
      clearTimeout(timer);
      el.classList.add('is-leaving');
      setTimeout(function () { el.remove(); }, 400);
    }
    return dismiss;
  }

  /* Dialogs */
  /* Remember whether the last input was a tap/click or the keyboard */
  var pointerInput = false;
  document.addEventListener('pointerdown', function () { pointerInput = true; }, true);
  document.addEventListener('keydown', function () { pointerInput = false; }, true);

  function openDialog(dialog, opener) {
    if (typeof dialog === 'string') dialog = document.getElementById(dialog);
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    /* A field marked autofocus (the search box) always gets the focus, so people can type straight away.
       Otherwise, opened by tap or click: focus the panel itself, so Safari doesn't ring the close button.
       Opened from the keyboard: focus stays on the first control, with its visible ring. */
    var autofocusField = dialog.querySelector('input[autofocus], textarea[autofocus]');
    if (autofocusField) {
      autofocusField.focus({ preventScroll: true });
    } else if (pointerInput) {
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
    root.classList.add('scroll-lock');
    emit('dialog:open', { id: dialog.id }, dialog);
  }

  function closeDialog(dialog) {
    if (typeof dialog === 'string') dialog = document.getElementById(dialog);
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.getAttribute('data-dialog-open'), opener);
      return;
    }
    var closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      var dialog = closer.closest('dialog');
      if (dialog) { event.preventDefault(); closeDialog(dialog); }
      return;
    }
    if (event.target.tagName === 'DIALOG' && event.target.open && event.target.classList.contains('dialog')) {
      /* Bottom sheets cover the screen with a clear dialog, so a tap counts as outside when it misses the panel */
      var box = event.target;
      if (box.matches('.sheet, .modal--sheet-mobile')) box = box.querySelector(':scope > .dialog__frame') || box;
      var rect = box.getBoundingClientRect();
      var inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener('close', function (event) {
    if (event.target.tagName !== 'DIALOG') return;
    if (!document.querySelector('dialog.dialog[open]')) root.classList.remove('scroll-lock');
    emit('dialog:close', { id: event.target.id }, event.target);
  }, true);

  /* Cart */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('[data-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.slice(0, 5);
  }

  function applySections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (!html) return;
      var doc = parseHTML(html);
      document.querySelectorAll('[data-cart-section="' + id + '"]').forEach(function (current) {
        var fresh = doc.querySelector('[data-cart-section="' + id + '"]');
        if (!fresh) return;
        var regions = current.querySelectorAll('[data-cart-region]');
        if (!regions.length) { current.innerHTML = fresh.innerHTML; return; }
        regions.forEach(function (region) {
          var name = region.getAttribute('data-cart-region');
          var next = fresh.querySelector('[data-cart-region="' + name + '"]');
          if (next) {
            var progress = {};
            region.querySelectorAll('[data-progress-key]').forEach(function (el) {
              progress[el.getAttribute('data-progress-key')] = el.style.getPropertyValue('--progress');
            });
            region.innerHTML = next.innerHTML;
            region.hidden = next.hidden;
            /* Progress lines grow from where they were instead of jumping */
            region.querySelectorAll('[data-progress-key]').forEach(function (el) {
              var before = progress[el.getAttribute('data-progress-key')];
              var after = el.style.getPropertyValue('--progress');
              if (!before || before === after) return;
              el.style.setProperty('--progress', before);
              requestAnimationFrame(function () { requestAnimationFrame(function () { el.style.setProperty('--progress', after); }); });
            });
          }
        });
      });
    });
    if (window.AromaMotion) window.AromaMotion.init(document);
  }

  function updateCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = count; });
    document.querySelectorAll('[data-cart-count-wrap]').forEach(function (el) {
      el.hidden = count === 0;
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
    document.querySelectorAll('[data-cart-count-label]').forEach(function (el) {
      var template = el.getAttribute('data-template');
      if (template) el.textContent = template.replace('[count]', count);
    });
  }

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) {
          var error = new Error(data.description || data.message || strings.addError);
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch((config.cartUrl || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  var cart = {
    add: function (items, options) {
      options = options || {};
      var sections = cartSectionIds();
      var body = { items: items };
      if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
      return request((config.cartAddUrl || '/cart/add') + '.js', body).then(function (data) {
        applySections(data.sections);
        return getCart().then(function (state) {
          updateCounts(state.item_count);
          emit('cart:updated', { cart: state, source: 'add', items: data.items });
          if (options.feedback !== false) cart.feedback();
          return state;
        });
      });
    },
    change: function (key, quantity) {
      var sections = cartSectionIds();
      var body = { id: key, quantity: quantity };
      if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
      return request((config.cartChangeUrl || '/cart/change') + '.js', body).then(function (state) {
        applySections(state.sections);
        updateCounts(state.item_count);
        emit('cart:updated', { cart: state, source: 'change' });
        return state;
      });
    },
    note: function (note) {
      return request((config.cartUpdateUrl || '/cart/update') + '.js', { note: note });
    },
    feedback: function () {
      var drawer = document.getElementById('CartDrawer');
      if (config.cartType === 'drawer' && drawer) {
        closeDialog(document.querySelector('[data-quick-add-dialog][open]'));
        openDialog(drawer);
      } else {
        toast(strings.addedToBag, { actionLabel: strings.viewBag, actionHref: config.cartUrl });
      }
    },
    refresh: function () {
      var sections = cartSectionIds();
      if (!sections.length) return getCart().then(function (state) { updateCounts(state.item_count); return state; });
      return fetch(window.location.pathname + '?sections=' + sections.join(','))
        .then(function (r) { return r.json(); })
        .then(function (data) { applySections(data); return getCart(); })
        .then(function (state) { updateCounts(state.item_count); return state; });
    }
  };

  /* Cart line controls (drawer and page) */
  var pendingLines = {};
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-line-remove], [data-line-step]');
    if (!button) return;
    var line = button.closest('[data-line-key]');
    if (!line) return;
    event.preventDefault();
    var key = line.getAttribute('data-line-key');
    var input = line.querySelector('[data-line-quantity]');
    var quantity = 0;
    if (button.hasAttribute('data-line-step')) {
      quantity = Math.max(0, (parseInt(input && input.value, 10) || 0) + parseInt(button.getAttribute('data-line-step'), 10));
      if (input) input.value = quantity;
    }
    changeLine(line, key, quantity);
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-line-quantity]');
    if (!input) return;
    var line = input.closest('[data-line-key]');
    changeLine(line, line.getAttribute('data-line-key'), Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* Enter in a quantity box updates that line; it must not submit the bag form, whose first button is checkout */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest('[data-line-quantity]');
    if (!input) return;
    event.preventDefault();
    input.blur();
  });

  /* While a change is on its way, totals and checkout dim so nobody pays from a stale total */
  var busyCount = 0;
  function setCartBusy(on) {
    busyCount = Math.max(0, busyCount + (on ? 1 : -1));
    document.querySelectorAll('[data-cart-section]').forEach(function (el) {
      el.classList.toggle('is-busy', busyCount > 0);
    });
  }

  function changeLine(line, key, quantity) {
    if (!pendingLines[key]) setCartBusy(true);
    clearTimeout(pendingLines[key]);
    line.classList.add('is-updating');
    line.setAttribute('aria-busy', 'true');
    pendingLines[key] = setTimeout(function () {
      cart.change(key, quantity).catch(function (error) {
        line.classList.remove('is-updating');
        line.removeAttribute('aria-busy');
        toast(error.message || strings.addError, { icon: false });
      }).then(function () {
        delete pendingLines[key];
        setCartBusy(false);
      });
    }, 260);
  }

  document.addEventListener('input', debounce(function (event) {
    var note = event.target.closest('[data-cart-note]');
    if (note) cart.note(note.value);
  }, 500));

  /* Variant picker */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('script[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.productRoot = this.closest('[data-product-root]') || this.parentElement;
      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    selectedOptions() {
      return Array.prototype.map.call(this.querySelectorAll('fieldset[data-option-index]'), function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        var select = fieldset.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }

    currentVariant() {
      var options = this.selectedOptions();
      return this.variants.find(function (variant) {
        return variant.options.every(function (value, index) { return value === options[index]; });
      }) || null;
    }

    updateAvailability() {
      var options = this.selectedOptions();
      var variants = this.variants;
      this.querySelectorAll('fieldset[data-option-index]').forEach(function (fieldset) {
        var index = parseInt(fieldset.getAttribute('data-option-index'), 10);
        fieldset.querySelectorAll('input').forEach(function (input) {
          var available = variants.some(function (variant) {
            if (!variant.available || variant.options[index] !== input.value) return false;
            return variant.options.every(function (value, i) { return i === index || options[i] == null || value === options[i]; });
          });
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }

    onChange() {
      var variant = this.currentVariant();
      this.updateAvailability();
      var legendValues = this.querySelectorAll('[data-selected-value]');
      var options = this.selectedOptions();
      legendValues.forEach(function (el, i) { el.textContent = options[i] || ''; });
      if (variant && this.hasAttribute('data-update-url') && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      emit('variant:change', { variant: variant, options: options }, this.productRoot);
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* Update price, buttons and labels inside a product root when the variant changes */
  /* Elements that belong to this product root: not inside a nested root, not marked static */
  function own(scope, selector) {
    return Array.prototype.filter.call(scope.querySelectorAll(selector), function (el) {
      var owner = el.closest('[data-product-root]');
      return (!owner || owner === scope) && !el.closest('[data-price-static]');
    });
  }

  document.addEventListener('variant:change', function (event) {
    var scope = event.target;
    var variant = event.detail.variant;
    own(scope, '[data-price-amount]').forEach(function (el) {
      if (variant) el.textContent = formatAmount(variant.price);
    });
    own(scope, '[data-price-from]').forEach(function (el) { el.hidden = true; });
    own(scope, '[data-price-compare]').forEach(function (el) {
      var onSale = variant && variant.compare_at_price && variant.compare_at_price > variant.price;
      el.hidden = !onSale;
      var amount = el.querySelector('[data-price-compare-amount]');
      if (onSale && amount) amount.textContent = formatAmount(variant.compare_at_price);
    });
    own(scope, '[data-variant-title]').forEach(function (el) {
      if (variant) el.textContent = variant.public_title || variant.title;
    });
    own(scope, 'input[name="id"]').forEach(function (input) {
      if (variant) { input.value = variant.id; input.disabled = false; }
    });
    own(scope, '[data-add-button]').forEach(function (button) {
      var label = button.querySelector('[data-add-label]') || button;
      if (!variant) {
        button.disabled = true;
        button.classList.add('is-soldout');
        label.textContent = strings.unavailable;
      } else if (!variant.available) {
        button.disabled = true;
        button.classList.add('is-soldout');
        label.textContent = strings.soldOut;
      } else {
        button.disabled = false;
        button.classList.remove('is-soldout');
        label.textContent = button.getAttribute('data-add-text') || strings.addToBag;
      }
    });
    own(scope, '.buy-buttons__now').forEach(function (el) {
      el.hidden = !variant || !variant.available;
    });
    if (variant && variant.featured_media) {
      emit('media:select', { mediaId: variant.featured_media.id }, scope);
    }
  });

  /* A link that opens a named row of Product details (data-detail) and scrolls to it, for example the returns line under the buy buttons. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-open-detail]');
    if (!link) return;
    var rows = document.querySelectorAll('details[data-detail="' + link.getAttribute('data-open-detail') + '"]');
    var row = Array.prototype.find.call(rows, function (el) { return el.offsetParent !== null; });
    if (!row) return;
    event.preventDefault();
    /* Fold the other open rows so the one asked for lands in view, even inside the sticky buy column. */
    Array.prototype.forEach.call(row.parentElement.querySelectorAll(':scope > details[open]'), function (other) {
      if (other !== row) other.open = false;
    });
    row.open = true;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    var summary = row.querySelector('summary');
    if (summary) summary.focus({ preventScroll: true });
  });

  /* Product form: add to the bag without leaving the page. "Buy now" adds the item and goes straight to checkout. */
  function itemFromForm(form) {
    var formData = new FormData(form);
    var item = { id: parseInt(formData.get('id'), 10), quantity: parseInt(formData.get('quantity'), 10) || 1 };
    var properties = {};
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      var buyNow = this.querySelector('[data-buy-now]');
      if (buyNow) buyNow.addEventListener('click', this.onBuyNow.bind(this));
    }

    onBuyNow(event) {
      event.preventDefault();
      var button = event.currentTarget;
      if (button.classList.contains('is-loading')) return;
      var error = this.querySelector('[data-form-error]');
      if (error) { error.hidden = true; error.textContent = ''; }
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      request((config.cartAddUrl || '/cart/add') + '.js', { items: [itemFromForm(this.form)] }).then(function () {
        window.location.href = (config.root || '/') + 'checkout';
      }).catch(function (err) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        if (error) { error.textContent = err.message; error.hidden = false; }
        else toast(err.message || strings.addError, { icon: false });
      });
    }

    onSubmit(event) {
      event.preventDefault();
      var button = this.form.querySelector('[type="submit"]');
      if (!button || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-loading')) return;
      var error = this.querySelector('[data-form-error]');
      if (error) { error.hidden = true; error.textContent = ''; }
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      var item = itemFromForm(this.form);
      var self = this;
      cart.add([item]).then(function () {
        emit('product:added', { variantId: item.id }, self);
      }).catch(function (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        else toast(err.message || strings.addError, { icon: false });
      }).finally(function () {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      });
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* Quantity input */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', this.onClick.bind(this));
      this.input && this.input.addEventListener('change', this.sync.bind(this));
      this.sync();
    }

    onClick(event) {
      var button = event.target.closest('[data-step]');
      if (!button || !this.input || this.closest('[data-line-key]')) return;
      event.preventDefault();
      var step = parseInt(button.getAttribute('data-step'), 10);
      var min = parseInt(this.input.min, 10) || 1;
      var max = parseInt(this.input.max, 10) || 9999;
      this.input.value = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    sync() {
      if (!this.input) return;
      var value = parseInt(this.input.value, 10) || 0;
      var min = parseInt(this.input.min, 10);
      if (isNaN(min)) min = 1;
      var minus = this.querySelector('[data-step="-1"]');
      if (minus && !this.closest('[data-line-key]')) minus.disabled = value <= min;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* Quick add from product cards */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-quick-add]');
    if (!trigger) return;
    event.preventDefault();
    var variantId = trigger.getAttribute('data-variant-id');
    if (variantId) {
      trigger.classList.add('is-loading');
      cart.add([{ id: parseInt(variantId, 10), quantity: 1 }])
        .catch(function (err) { toast(err.message || strings.addError, { icon: false }); })
        .finally(function () { trigger.classList.remove('is-loading'); });
      return;
    }
    var dialog = document.querySelector('[data-quick-add-dialog]');
    var content = dialog && dialog.querySelector('[data-quick-add-content]');
    if (!dialog || !content) { window.location.href = trigger.getAttribute('data-quick-add'); return; }
    var url = trigger.getAttribute('data-quick-add');
    trigger.classList.add('is-loading');
    quickAddRequest(url)
      .then(function (html) {
        var doc = parseHTML(html);
        var body = doc.querySelector('[data-quick-add-body]') || doc.body;
        content.innerHTML = body.innerHTML;
        openDialog(dialog, trigger);
        if (window.AromaMotion) window.AromaMotion.init(content);
        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
      })
      .catch(function () { window.location.href = url; })
      .finally(function () { trigger.classList.remove('is-loading'); });
  });

  /* Horizontal rail with buttons and a progress line */
  class RailSlider extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.rail');
      if (!this.rail) return;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.bar = this.querySelector('.rail-progress__bar');
      /* The bar follows the scroll on its own where the browser can tie it to the row's scroll
         (CSS scroll timeline); otherwise it moves once per frame. Either way no transition, which
         Safari used to restart on every scroll event and so lagged behind the finger. */
      this.scrollLinked = !!(window.CSS && CSS.supports && CSS.supports('timeline-scope', '--a'));
      this.update = this.update.bind(this);
      this.measure = this.measure.bind(this);
      this.onScroll = () => { if (this.frame) return; this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); }); };
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.measure);
      this.measure();
      this.prev && this.prev.addEventListener('click', this.go.bind(this, -1));
      this.next && this.next.addEventListener('click', this.go.bind(this, 1));
      this.update();
    }

    disconnectedCallback() { window.removeEventListener('resize', this.measure); }

    measure() {
      var ratio = this.rail.scrollWidth ? this.rail.clientWidth / this.rail.scrollWidth : 1;
      var rtl = getComputedStyle(this.rail).direction === 'rtl';
      this.ratio = ratio;
      this.travel = (1 / ratio - 1) * 100 * (rtl ? -1 : 1);
      this.style.setProperty('--rail-thumb', (ratio * 100).toFixed(2) + '%');
      this.style.setProperty('--rail-travel', this.travel.toFixed(2) + '%');
      this.classList.toggle('is-scroll-linked', this.scrollLinked);
      this.update();
    }

    go(direction) {
      var item = this.rail.firstElementChild;
      var gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      var step = item ? item.getBoundingClientRect().width + gap : this.rail.clientWidth * 0.8;
      var rtl = getComputedStyle(this.rail).direction === 'rtl';
      this.rail.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: 'smooth' });
    }

    update() {
      var max = this.rail.scrollWidth - this.rail.clientWidth;
      var position = Math.abs(this.rail.scrollLeft);
      var progress = max > 0 ? position / max : 0;
      if (this.bar && !this.scrollLinked) this.bar.style.transform = 'translate3d(' + (this.travel * progress).toFixed(2) + '%, 0, 0)';
      var isStatic = max <= 2;
      if (this.classList.contains('is-static') !== isStatic) this.classList.toggle('is-static', isStatic);
      if (this.prev) this.prev.disabled = position <= 2;
      if (this.next) this.next.disabled = position >= max - 2;
    }
  }
  if (!customElements.get('rail-slider')) customElements.define('rail-slider', RailSlider);

  /* Language switch: links submit the localization form */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-language-code]');
    if (!link) return;
    var form = link.closest('form') || document.querySelector('form[data-localization-form]');
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector('input[name="language_code"]');
    if (input) input.value = link.getAttribute('data-language-code');
    form.submit();
  });

  /* Copy to clipboard */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-copy]');
    if (!button || !navigator.clipboard) return;
    event.preventDefault();
    navigator.clipboard.writeText(button.getAttribute('data-copy')).then(function () {
      var label = button.querySelector('[data-copy-label]') || button;
      var original = label.textContent;
      label.textContent = strings.copied;
      setTimeout(function () { label.textContent = original; }, 1800);
    });
  });

  /* Videos: no data until a video comes near the screen; autoplaying ones play only while
     visible and never for people who prefer less motion. */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var wrapper = entry.target;
      var video = wrapper.querySelector('video');
      if (!video || !wrapper.hasAttribute('data-autoplay')) return;
      if (entry.isIntersecting) {
        if (reduceMotion.matches || wrapper.dataset.userPaused) return;
        video.muted = true;
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  }, { rootMargin: '200px 0px' }) : null;

  function initVideos(scope) {
    (scope || document).querySelectorAll('[data-lazy-video]').forEach(function (wrapper) {
      if (wrapper.dataset.videoReady) return;
      wrapper.dataset.videoReady = 'true';
      if (videoObserver) videoObserver.observe(wrapper);
    });
  }

  /* YouTube and Vimeo: the player loads only when someone taps the poster */
  document.addEventListener('click', function (event) {
    var facade = event.target.closest('[data-video-facade]');
    if (!facade) return;
    var frame = document.createElement('iframe');
    frame.className = 'lazy-video__media';
    frame.src = facade.getAttribute('data-embed');
    frame.title = facade.getAttribute('data-title') || '';
    frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    frame.allowFullscreen = true;
    var holder = document.createElement('div');
    holder.className = facade.className.replace('lazy-video--facade', '');
    holder.appendChild(frame);
    facade.replaceWith(holder);
    frame.focus();
  });

  /* Quick add: start fetching when someone hovers, focuses or touches the button,
     so the dialog opens straight away on click. */
  var quickAddCache = {};
  function quickAddRequest(url) {
    if (!quickAddCache[url]) {
      quickAddCache[url] = fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=quick-add')
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .catch(function (error) { delete quickAddCache[url]; throw error; });
    }
    return quickAddCache[url];
  }
  ['pointerenter', 'focusin', 'touchstart'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var trigger = event.target.closest && event.target.closest('[data-quick-add]:not([data-variant-id])');
      if (trigger) quickAddRequest(trigger.getAttribute('data-quick-add')).catch(function () {});
    }, { passive: true, capture: type === 'pointerenter' });
  });

  /* Images: start lazy images well before they scroll into view (about 2.5 screens ahead), so
     they are already there when people arrive. Whole sections are watched, not single images,
     because images inside unveil animations are clipped and would otherwise only start loading
     once they are on screen. Starts after the page has loaded so it never slows the first screen,
     and skips anything inside closed menus, dialogs and panels. */
  var sectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      sectionObserver.unobserve(entry.target);
      entry.target.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        if (img.getClientRects().length === 0 || img.closest('dialog:not([open]), details:not([open]), [hidden]')) return;
        img.loading = 'eager';
      });
    });
  }, { rootMargin: '250% 0px 250% 0px' }) : null;

  function initImages(scope) {
    if (!sectionObserver) return;
    scope = scope || document;
    var sections = scope.matches && scope.matches('.shopify-section') ? [scope] : scope.querySelectorAll('.shopify-section');
    Array.prototype.forEach.call(sections, function (section) { sectionObserver.observe(section); });
  }

  function startImages() { initImages(document); }
  if (document.readyState === 'complete') startImages();
  else window.addEventListener('load', startImages, { once: true });
  document.addEventListener('shopify:section:load', function (event) { initImages(event.target); });
  document.addEventListener('DOMContentLoaded', function () { initVideos(document); });
  document.addEventListener('shopify:section:load', function (event) { initVideos(event.target); });

  Aroma.debounce = debounce;
  Aroma.emit = emit;
  Aroma.parseHTML = parseHTML;
  Aroma.formatMoney = formatMoney;
  Aroma.formatAmount = formatAmount;
  Aroma.toast = toast;
  Aroma.openDialog = openDialog;
  Aroma.closeDialog = closeDialog;
  Aroma.cart = cart;
  Aroma.updateCounts = updateCounts;
  Aroma.applySections = applySections;
})();
